
import React, { useState, useEffect } from "react";
// ... (imports remain mostly the same)
import { useAuth } from "../context/AuthContext";
import { ApprovalRequest } from "../types/legalOps";
import {
  listApprovalRequestsByModule,
  deleteApprovalRequest,
} from "../services/approvalRequestsService";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { LegalListModule } from "../modules/legal/LegalListModule";
import { contractModuleConfig } from "../modules/legal/config/ContractModuleConfig";
import TemplatesPage from "./TemplatesPage"; 
import { ContractReviewForm } from "../modules/legal/forms/ContractReviewForm";
import { SignRequestDetailModal } from "../modules/sign/components/SignRequestDetailModal";
import {
  canInitiateLegalRequest,
  canManageAllRequests,
  isDeptMinimal,
  getDeptCodeByEmail,
  isLegal,
  isManager,
  isSuperAdmin,
  canViewAllData,
  isSuperUser,
} from "../config/roles";
import { getDeptNameByCode } from "../constants/departments";

const HISTORY_STATUSES = [
  'APPROVED', 'FULLY_APPROVED', 'REJECTED', 'RETURNED', 
  'LEGAL_RETURNED', 'PUBLISHED', 'COMPLETED', 'CANCELLED'
];

const getStatusLabel = (status: string) => {
  switch (status) {
    case "SUBMITTED": return "已送件";
    case "IN_REVIEW": return "審查中";
    case "WAITING_APPROVAL": return "主管核決中";
    case "APPROVED": return "已核准";
    case "FULLY_APPROVED": return "流程完成";
    case "REJECTED": return "已駁回";
    case "RETURNED": return "退回";
    case "LEGAL_RETURNED": return "法務退回";
    case "PUBLISHED": return "已公告";
    case "PENDING_REVIEW": return "簽核中";
    case "DRAFT": return "草稿";
    case "PENDING_MANAGER": return "待主管核決";
    case "PENDING_LEGAL": return "待法務審查";
    default: return status || "未知狀態";
  }
};

const getStatusColor = (status: string) => {
  if (status === "FULLY_APPROVED" || status === "APPROVED" || status === "PUBLISHED") return "var(--ok)";
  if (status === "REJECTED" || status === "RETURNED" || status === "LEGAL_RETURNED") return "var(--danger)";
  if (status === "WAITING_APPROVAL" || status === "PENDING_MANAGER") return "#8b5cf6";
  if (status === "SUBMITTED" || status === "IN_REVIEW" || status === "PENDING_LEGAL") return "#fbbf24";
  return "#666";
};

export function ContractReviewsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"review" | "retention" | "templates">("review");
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);

  const canSeeRetention = isLegal(user?.email) || isManager(user?.email) || isSuperAdmin(user?.email);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 16 }}>
        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "review" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "review" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("review")}
        >
          合約審查 (送件與簽核)
        </button>
        {canSeeRetention && (
          <button
            className="navbtn"
            style={{
              width: "auto",
              borderBottom: activeTab === "retention" ? "2px solid #fff" : "none",
              borderRadius: 0,
              color: activeTab === "retention" ? "#fff" : "var(--muted)",
            }}
            onClick={() => setActiveTab("retention")}
          >
            合約留存管理 (資料庫)
          </button>
        )}
        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "templates" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "templates" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("templates")}
        >
          制式合約範本庫
        </button>
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {activeTab === "review" && (
          <TabReviewSystem
            onSelectRequest={(req) => setSelectedRequest(req)}
          />
        )}
        {activeTab === "retention" && canSeeRetention && (
          <LegalListModule config={{ ...contractModuleConfig, canEdit: true }} />
        )}
        {activeTab === "templates" && <TemplatesPage />}
      </div>

      {selectedRequest && (
          <SignRequestDetailModal 
              request={selectedRequest}
              onClose={() => setSelectedRequest(null)}
              onUpdate={() => { setSelectedRequest(null); /* triggers re-fetch implicitly */ }}
          />
      )}
    </div>
  );
}

function TabReviewSystem({ onSelectRequest }: { onSelectRequest: (r: ApprovalRequest) => void }) {
  const { user, debugViewDepartmentId } = useAuth();
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();
  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ApprovalRequest | null>(null);
  const [subTab, setSubTab] = useState<'ACTIVE' | 'HISTORY'>('ACTIVE');

  useEffect(() => {
    if (!user) return;
    const isDeptAccount = isDeptMinimal(user.email);
    const effectiveDeptCode = debugViewDepartmentId || (isDeptAccount ? getDeptCodeByEmail(user.email) : null);

    const unsub = listApprovalRequestsByModule("contractDraft", (all) => {
        let filtered = all;
        if (canViewAllData(user.email) && !debugViewDepartmentId) {
            filtered = all;
        } else if (effectiveDeptCode) {
            const deptName = getDeptNameByCode(effectiveDeptCode);
            filtered = all.filter((r) => r.department === deptName || r.visibleDepartments?.includes(deptName));
        } else {
            filtered = all.filter((r) => r.requesterUid === user.id);
        }
        setItems(filtered);
      });
    return () => { unsub.then((fn) => fn()); };
  }, [user, debugViewDepartmentId]);

  const handleDelete = (req: ApprovalRequest) => {
      requestDelete({
        targetType: "contractReview",
        targetId: req.id,
        summary: req.title,
        onConfirm: async () => {
            await deleteApprovalRequest(req.id);
            showToast("Deleted");
        }
      })
  };

  const activeList = items.filter(i => !HISTORY_STATUSES.includes(i.status));
  const historyList = items.filter(i => HISTORY_STATUSES.includes(i.status));
  const displayList = subTab === 'ACTIVE' ? activeList : historyList;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="row" style={{ gap: 0 }}>
                <button className={`navbtn ${subTab==='ACTIVE'?'active':''}`} onClick={() => setSubTab('ACTIVE')}>進行中 ({activeList.length})</button>
                <button className={`navbtn ${subTab==='HISTORY'?'active':''}`} onClick={() => setSubTab('HISTORY')}>歷史紀錄 ({historyList.length})</button>
            </div>
            {canInitiateLegalRequest(user?.role||'viewer') && (
                <button className="btn" style={{ width: "auto" }} onClick={() => { setEditingItem(null); setIsFormOpen(true); }}>+ 提出合約審查</button>
            )}
        </div>

        <div style={{ flex: 1, overflow: "auto", background: "#161616", border: "1px solid var(--border)", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ background: "#222", color: "#aaa", position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                        <th style={{ padding: 12 }}>單號</th>
                        <th style={{ padding: 12 }}>標題</th>
                        <th style={{ padding: 12 }}>部門</th>
                        <th style={{ padding: 12 }}>狀態</th>
                        <th style={{ padding: 12, textAlign: "right" }}>操作</th>
                    </tr>
                </thead>
                <tbody>
                    {displayList.map(item => (
                        <tr key={item.id} style={{ borderBottom: "1px solid #333" }}>
                            <td style={{ padding: 12, color: "#a5b4fc" }}>{item.docCode || item.id.slice(0,8)}</td>
                            <td style={{ padding: 12, fontWeight: 500 }}>{item.title}</td>
                            <td style={{ padding: 12 }}>{item.department} <span style={{color:'#888'}}>{(item as any).handlerName || item.requesterName}</span></td>
                            <td style={{ padding: 12 }}>
                                <span className="badge" style={{ color: getStatusColor(item.status), borderColor: getStatusColor(item.status) }}>
                                    {getStatusLabel(item.status)}
                                </span>
                            </td>
                            <td style={{ padding: 12, textAlign: "right" }}>
                                <button className="smallbtn" onClick={() => onSelectRequest(item)}>檢視</button>
                                {(isSuperUser(user?.email) || item.status === 'DRAFT') && (
                                    <button className="smallbtn" style={{ marginLeft: 8, color: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => handleDelete(item)}>刪除</button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {displayList.length === 0 && (
                        <tr><td colSpan={5} style={{padding: 40, textAlign: 'center', color: 'var(--muted)'}}>無資料</td></tr>
                    )}
                </tbody>
            </table>
        </div>

        {isFormOpen && (
            <ContractReviewForm 
                initialData={editingItem} 
                onClose={() => setIsFormOpen(false)} 
                onSuccess={() => { setIsFormOpen(false); showToast("Success"); }} 
                allExisting={items}
            />
        )}
    </div>
  );
}
